import { Outlet } from "react-router-dom";
import { Suspense, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PageLoader } from "@/components/ui/page-loader";
import { useAuth } from "@/auth/session";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  UsersRound,
  Calendar,
  Heart,
  Utensils,
  Send,
  DollarSign,
  Settings,
  LogOut,
  Menu,
  Upload,
  ClipboardList,
  Layers,
  ChevronDown,
  ChevronRight,
  Sparkles,
  CalendarClock,
  UserCheck,
} from "lucide-react";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { CricketIcon } from "@/components/ui/cricket-icon";

export function DashboardLayout() {
  const { user, logoutMutation } = useAuth();
  const location = useLocation();
  const shouldShowBanner = user && user.emailVerified === false;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  // Coach-review approve/reject is isStaff-gated server-side (coaches can
  // clear their own academy's queue, not just admins) — this nav link
  // needs to be visible to coaches too, unlike the isAdmin-only slice
  // below it.
  const isStaffUser = isAdmin || user?.role === "coach";

  type NavChild = { name: string; href: string; testId: string };
  type NavItem = {
    name: string;
    icon: typeof LayoutDashboard;
    testId: string;
    href?: string;
    children?: NavChild[];
  };

  const navigation: NavItem[] = [
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, testId: "link-dashboard" },
    { name: "Players", href: "/dashboard/team", icon: Users, testId: "link-players" },
    ...(isAdmin
      ? [
          { name: "Import Roster", href: "/dashboard/admin/players/import", icon: Upload, testId: "link-import-roster" },
          { name: "AI Task Queue", href: "/dashboard/admin/ai-tasks", icon: Sparkles, testId: "link-ai-task-queue" },
          { name: "Agent Schedules", href: "/dashboard/admin/agent-schedules", icon: CalendarClock, testId: "link-agent-schedules" },
        ]
      : []),
    ...(isStaffUser
      ? [
          { name: "Pending Review", href: "/dashboard/admin/players/pending-review", icon: UserCheck, testId: "link-pending-review" },
        ]
      : []),
    { name: "My Teams", href: "/coach/teams", icon: UsersRound, testId: "link-teams" },
    { name: "Training Sessions", href: "/coach/sessions", icon: ClipboardList, testId: "link-sessions" },
    {
      name: "Training Groups",
      icon: Layers,
      testId: "link-batch-management",
      children: [
        { name: "Record Session", href: "/coach/batch-entry", testId: "link-batch-entry" },
        { name: "My Squads", href: "/coach/batches", testId: "link-batches" },
      ],
    },
    { name: "Schedule", href: "/dashboard/schedule", icon: Calendar, testId: "link-schedule" },
    { name: "Fitness Tracking", href: "/dashboard/fitness", icon: Heart, testId: "link-fitness" },
    { name: "Meal Plans", href: "/dashboard/meal-plans", icon: Utensils, testId: "link-meal-plans" },
    { name: "Announcements", href: "/dashboard/announcements", icon: Send, testId: "link-announcements" },
    { name: "Payments", href: "/dashboard/payments", icon: DollarSign, testId: "link-payments" },
    { name: "Settings", href: "/dashboard/settings", icon: Settings, testId: "link-settings" },
  ];

  const Logo = () => (
    <Link to="/dashboard">
      <div className="flex items-center gap-2 font-bold text-xl text-primary cursor-pointer">
        <CricketIcon className="h-6 w-6 text-white bg-primary p-1 rounded" />
        <span>Legacy Cricket</span>
      </div>
    </Link>
  );

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 md:p-6 border-b border-gray-200">
        <Logo />
        <p className="text-gray-600 text-sm mt-2">Coach Dashboard</p>
      </div>

      {/* Coach Profile */}
      <div className="p-4 border-b border-gray-200">
        <Link 
          to="/profile" 
          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
          onClick={() => setIsMobileNavOpen(false)}
        >
          <Avatar className="h-10 w-10">
            {user?.profileImage ? (
              <AvatarImage src={user.profileImage} alt={user?.fullName || "User"} />
            ) : null}
            <AvatarFallback>
              {user?.fullName ? getInitials(user.fullName) : "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold truncate">{user?.fullName || "User"}</h4>
            <p className="text-xs text-gray-600">
              {user?.role === "coach" ? "Head Coach" : user?.role === "admin" ? "Administrator" : "Coach"}
            </p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 md:py-4 space-y-1" data-testid="legacy-sidebar-nav">
        {navigation.map((item) => {
          const Icon = item.icon;

          // Expandable group (e.g. Batch Management)
          if (item.children) {
            const childActive = item.children.some(
              (child) => location.pathname === child.href
            );
            const isOpen = openGroups[item.name] ?? childActive;

            return (
              <div key={item.name}>
                <button
                  type="button"
                  data-testid={item.testId}
                  aria-expanded={isOpen}
                  onClick={() =>
                    setOpenGroups((prev) => ({
                      ...prev,
                      [item.name]: !(prev[item.name] ?? childActive),
                    }))
                  }
                  className={cn(
                    "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    childActive
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span className="flex-1 text-left">{item.name}</span>
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                {isOpen && (
                  <div className="mt-1 ml-4 space-y-1 border-l border-gray-200 pl-3">
                    {item.children.map((child) => {
                      const isChildActive = location.pathname === child.href;
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          data-testid={child.testId}
                          onClick={() => setIsMobileNavOpen(false)}
                        >
                          <div
                            className={cn(
                              "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                              isChildActive
                                ? "bg-primary text-white"
                                : "text-gray-700 hover:bg-gray-100"
                            )}
                          >
                            {child.name}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const isActive = location.pathname === item.href;

          return (
            <Link
              key={item.name}
              to={item.href ?? "#"}
              data-testid={item.testId}
              onClick={() => setIsMobileNavOpen(false)}
            >
              <div
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100"
                )}
              >
                <Icon className="h-5 w-5" />
                <span>{item.name}</span>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Sidebar Footer */}
      <div className="border-t border-gray-200 p-4">
        <Button
          variant="outline"
          className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => logoutMutation.mutate()}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="legacy-dashboard-page">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:border-r bg-white" data-testid="legacy-dashboard-sidebar">
        <SidebarContent />
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Top Bar - Only visible on mobile */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white md:hidden">
          <span className="font-semibold text-gray-900">Coach Dashboard</span>
          <button
            onClick={() => setIsMobileNavOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-md"
            data-testid="legacy-dashboard-mobile-menu-button"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5 text-gray-700" />
          </button>
        </div>

        {/* Desktop Header - Only visible on desktop */}
        <header className="hidden md:flex bg-white border-b border-gray-200 px-6 py-4 items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900" data-testid="legacy-dashboard-title">Coach Dashboard</h1>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              {user?.fullName || "User"}
            </span>
          </div>
        </header>

        {/* Email Verification Banner */}
        {shouldShowBanner && <VerifyEmailBanner />}

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto px-4 py-6 md:px-6 md:py-8">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Mobile Drawer + Backdrop */}
      {isMobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setIsMobileNavOpen(false)}
        />
      )}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 flex flex-col transform bg-white shadow-lg transition-transform md:hidden",
          isMobileNavOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="legacy-dashboard-mobile-drawer"
      >
        <SidebarContent />
      </div>
    </div>
  );
}
